import CloseIcon from '@mui/icons-material/Close';
import PersonAddTwoToneIcon from '@mui/icons-material/PersonAddTwoTone';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CardMedia,
  Grid,
  IconButton,
  Modal,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  IconBriefcase,
  IconBuildingCommunity,
  IconCalendar,
  IconCalendarEvent,
  IconClock,
  IconHeadset,
  IconHeartHandshake,
  IconInbox,
  IconListCheck,
  IconUser
} from '@tabler/icons-react';
import axiosInstance from 'Services/APIService';
import { AppDispatch, RootState } from 'app/store';
import { useDispatch, useSelector } from 'react-redux';
import { fetchParticipantsList, setSelectedClient } from 'slices/HomeTilesSlices';
import { ThemeMode } from 'types/config';
import useAuth from 'hooks/useAuth';
import useConfig from 'hooks/useConfig';
import { gridSpacing } from 'store/constant';
import MainCard from 'ui-component/cards/MainCard';
import ImagePlaceholder from 'ui-component/cards/Skeleton/ImagePlaceholder';
import Avatar from 'ui-component/extended/Avatar';
import Cover from 'assets/images/profile/img-profile-bg.png';
import User1 from 'assets/images/users/img-user.png';
import CalenderEvents from '../../../src/components/NDIS_section/calendarEvents/CalenderEvents';
import ListOfManagers from './ListOfManagers';
import OfficeSupport from './OfficeSupport';
import ShiftNotes from './shiftDetails/ShiftNotes';
import CreateStaffMembers from './staffManagement/CreateStaffMembers';
import SupportWorkersList from './SupportWorkersList';
import UserProfile from './UserProfile';
import WorkersList from './WorkersList';

// Tab accessibility function
const a11yProps = (index: number) => ({
  id: `simple-tab-${index}`,
  'aria-controls': `simple-tabpanel-${index}`
});

// Tab options
const tabOptions = [
  {
    to: '/profile/user-info',
    icon: <IconUser stroke={1.5} size="17px" />,
    label: 'User Profile',
    value: 'user-profile',
    isDisable: false
  },
  {
    to: '/profile/weekly-patients-schedule',
    icon: <IconCalendar stroke={1.5} size="17px" />,
    label: 'Weekly Patients Schedule',
    value: 'weekly-patients-schedule',
    isDisable: false
  },
  {
    to: '/profile/managers',
    icon: <IconBriefcase stroke={1.5} size="17px" />,
    label: 'Managers',
    value: 'managers',
    isDisable: false
  },
  {
    to: '/profile/office-support',
    icon: <IconHeadset stroke={1.5} size="17px" />,
    label: 'office support',
    value: 'office-support',
    isDisable: false
  },
  {
    to: '/profile/support-workers',
    icon: <IconHeartHandshake stroke={1.5} size="17px" />,
    label: 'Support Workers',
    value: 'support-workers',
    isDisable: false
  },
  {
    to: '/profile/ShiftNotes',
    icon: <IconInbox stroke={1.5} size="17px" />,
    label: 'Shift-Notes',
    value: 'Shift-Notes',
    isDisable: false
  }
];

// Define the type for the selected client
interface Client {
  label: string;
  id: number;
  email: string;
  avatar?: string;
  ndisNumber?: string;
}

// Create the context
const ClientContext = createContext<{
  selectedClient: Client | null;
  setSelectedClient: (client: Client | null) => void;
}>({
  selectedClient: null,
  setSelectedClient: () => {}
});

// Custom hook to use the context
export const useClientContext = () => useContext(ClientContext);

const ProfileMainPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { borderRadius } = useConfig();
  const dispatch = useDispatch<AppDispatch>();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  // Retrieve selectedClient from Redux
  const selectedClient = useSelector((state: RootState) => state.homeTilesServices.selectedClient);

  const [value, setValue] = useState('user-profile');
  const [isLoading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isClientsLoading, setIsClientsLoading] = useState(true);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [hasValidated, setHasValidated] = useState(false);

  const loginInfo = localStorage.getItem('user-details');
  const userDetails = loginInfo ? JSON.parse(loginInfo) : null;

  const participantsList = useSelector(
    (state: RootState) => state?.homeTilesServices?.participants
  ) || { participants_list: [] };

  // Update clientsList to handle new API response structure
  const clientsList = useMemo(
    () =>
      participantsList.participants_list.map(
        (
          participant: { name: string; email: string; avatar?: string; ndisNumber?: string },
          index: number
        ) => ({
          label: participant.name,
          id: index + 1,
          email: participant.email,
          avatar: participant.avatar || '',
          ndisNumber: participant.ndisNumber || ''
        })
      ),
    [participantsList.participants_list]
  );

  const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}') as {
    projectId?: string;
    projectName?: string;
    isAdmin?: boolean;
  };

  const isAdminLogin =
    selectedProject?.isAdmin === true &&
    userDetails?.role?.some((r: any) => r.label.toLowerCase() === 'admin');

  const selectedProjectId = useSelector(
    (state: RootState) => state?.homeTilesServices?.selectedProjectId
  );
  const selectedServiceTitle = useSelector(
    (state: RootState) => state?.serviceSelected?.selectedServiceTitle
  );
  const email = userDetails?.email || '';
  const role = userDetails?.role ? [userDetails.role] : [''];

  // On component mount, set loading state to false
  useEffect(() => setLoading(false), []);

  // Save selected client to localStorage (excluding avatar) and dispatch to Redux
  useEffect(() => {
    if (selectedClient) {
      const persistClient = {
        label: selectedClient.label,
        id: selectedClient.id,
        email: selectedClient.email,
        ndisNumber: selectedClient.ndisNumber
      };
      try {
        localStorage.setItem('selectedClient', JSON.stringify(persistClient));
      } catch (error) {
        console.error('Failed to save selectedClient to localStorage:', error);
        localStorage.removeItem('selectedClient');
      }
    } else {
      localStorage.removeItem('selectedClient');
    }
  }, [selectedClient]);

  // Validate selectedClient against clientsList
  useEffect(() => {
    if (
      !hasValidated &&
      selectedClient &&
      !isClientsLoading &&
      !clientsError &&
      clientsList.length > 0
    ) {
      const isValidClient = clientsList.some(
        (client) =>
          client.id === selectedClient.id &&
          client.label === selectedClient.label &&
          client.email === selectedClient.email &&
          client.ndisNumber === selectedClient.ndisNumber
      );
      if (!isValidClient) {
        dispatch(setSelectedClient(null));
        localStorage.removeItem('selectedClient');
        setSnackbarMessage(
          'The previously selected client is no longer available. Please select a new client.'
        );
      }
      setHasValidated(true);
      setIsInitialLoad(false);
    } else if (!hasValidated && !isClientsLoading && !clientsError && clientsList.length > 0) {
      setHasValidated(true);
      setIsInitialLoad(false);
    }
  }, [clientsList, selectedClient, hasValidated, isClientsLoading, clientsError, dispatch]);

  // Fetch clients
  useEffect(() => {
    if (selectedProjectId && selectedServiceTitle && email && role.length > 0) {
      setIsClientsLoading(true);
      dispatch(
        fetchParticipantsList({
          group_id: selectedProjectId || '',
          service_name_id: selectedServiceTitle || '',
          nested_component_id: pathname,
          email: isAdminLogin ? '' : email,
          roles: role,
          client_name: ''
        })
      )
        .unwrap()
        .then(() => {
          setIsClientsLoading(false);
        })
        .catch((error) => {
          setIsClientsLoading(false);
          setClientsError('Failed to load clients. Please try again later.');
        });
    } else {
      setIsClientsLoading(false);
    }
  }, [selectedProjectId, selectedServiceTitle]);

  // Fetch avatar
  useEffect(() => {
    const fetchAvatar = async () => {
      if (userDetails?.avatar) {
        try {
          const response = await axiosInstance.get(
            `/avatar/get-avatar?path=${encodeURIComponent(userDetails.avatar)}`,
            { responseType: 'blob' }
          );
          const imageUrl = URL.createObjectURL(response.data);
          setAvatarUrl(imageUrl);
        } catch (error) {
          console.error('Error fetching avatar:', error);
          setAvatarUrl(null);
        }
      }
    };

    fetchAvatar();

    // Cleanup object URL to prevent memory leaks
    return () => {
      if (avatarUrl) {
        URL.revokeObjectURL(avatarUrl);
      }
    };
  }, [userDetails?.avatar]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    if (!selectedClient) {
      setSnackbarMessage('Please select a client first.');
      return;
    }
    setValue(newValue);
    navigate(`/profile/${newValue}`);
  };

  useEffect(() => {
    switch (pathname) {
      case '/profile/user-info':
        setValue('user-profile');
        break;
      case '/profile/weekly-patients-schedule':
        setValue('weekly-patients-schedule');
        break;
      case '/profile/managers':
        setValue('managers');
        break;
      case '/profile/workers':
        setValue('workers');
        break;
      case '/profile/office-support':
        setValue('office-support');
        break;
      case '/profile/support-workers':
        setValue('support-workers');
        break;
      case '/profile/ShiftNotes':
        setValue('Shift-Notes');
        break;
      default:
        setValue('user-profile');
    }
  }, [pathname]);

  const navItems = [
    {
      label: 'Clock In/Out',
      icon: <IconClock size={24} color="#1976d2" />,
      link: '/clockInOut',
      color: 'primary'
    },
    {
      label: 'Taskboard',
      icon: <IconListCheck size={24} color="#f57c00" />,
      link: '/taskboard',
      color: 'warning'
    },
    {
      label: 'CalendarShifts',
      icon: <IconCalendarEvent size={24} color="#0288d1" />,
      link: '/CalenderShifts',
      color: 'info'
    },
    ...(isAdminLogin
      ? [
          {
            label: 'NDIS Operations',
            icon: <IconBuildingCommunity size={24} color="#388e3c" />,
            description: 'Access and manage all NDIS-related operations for streamlined workflows.',
            link: '/ndis-operations',
            color: 'success'
          }
        ]
      : [])
  ];

  const handleClickMessages = () => {
    if (!selectedClient) {
      setSnackbarMessage('Please select a client first.');
      return;
    }
    navigate('Messages');
  };

  const handleClickReport = () => {
    if (!selectedClient) {
      setSnackbarMessage('Please select a client first.');
      return;
    }
    navigate('report-all');
  };

  const handleOpenModal = () => {
    if (!isAdminLogin) {
      setSnackbarMessage('Only admins can create staff members.');
      return;
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleNavItemClick = (link: string) => {
    if (isAdminLogin && link === '/ndis-operations') {
      navigate(link);
      return;
    }
    if (!selectedClient) {
      setSnackbarMessage('Please select a client first.');
      return;
    }
    navigate(link, { state: { selectedClient } });
  };

  const handleClientChange = (event: any, newValue: Client | null) => {
    if (!isInitialLoad) {
      dispatch(setSelectedClient(newValue));
    }
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarMessage(null);
  };

  return (
    <ClientContext.Provider
      value={{ selectedClient, setSelectedClient: (client) => dispatch(setSelectedClient(client)) }}
    >
      {/* Persistent Alert when no client is selected */}
      {!selectedClient && !isClientsLoading && !clientsError && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="warning">
            Please select a client from the dropdown to access page functionalities.
          </Alert>
        </Box>
      )}

      <Grid container spacing={2}>
        {/* Main Content Section */}
        <Grid item xs={12} md={8} lg={9}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '16px',
              mb: 1,
              justifyContent: 'left',
              '@media (max-width: 600px)': {
                justifyContent: 'center'
              }
            }}
          >
            {navItems.map((item) => (
              <Box
                key={item.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                  padding: '4px 14px',
                  cursor:
                    (isAdminLogin && item.link === '/ndis-operations') || selectedClient
                      ? 'pointer'
                      : 'not-allowed',
                  transition: 'background-color 0.3s, transform 0.2s',
                  '&:hover':
                    (isAdminLogin && item.link === '/ndis-operations') || selectedClient
                      ? {
                          backgroundColor: '#e3f2fd',
                          transform: 'scale(1.05)'
                        }
                      : {},
                  minWidth: '100px',
                  width: 'auto',
                  maxWidth: '150px',
                  height: 'auto',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  opacity:
                    (isAdminLogin && item.link === '/ndis-operations') || selectedClient ? 1 : 0.6,
                  '@media (max-width: 600px)': {
                    width: '124px',
                    padding: '2px'
                  }
                }}
                onClick={() => handleNavItemClick(item.link)}
              >
                {React.isValidElement(item.icon) &&
                  React.cloneElement(item.icon as React.ReactElement<any>, {
                    style: { fontSize: '12px', marginBottom: '2px', marginRight: '4px' }
                  })}
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '12px',
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>

        {/* Autocomplete Section - Visible for all users */}
        <Grid item xs={12} md={4} lg={3}>
          <Box>
            {isClientsLoading ? (
              <TextField
                label="Loading clients..."
                variant="outlined"
                fullWidth
                size="small"
                disabled
              />
            ) : clientsError ? (
              <Alert severity="error">{clientsError}</Alert>
            ) : (
              <Autocomplete<Client>
                disablePortal
                options={clientsList}
                value={selectedClient}
                onChange={handleClientChange}
                getOptionLabel={(option) => option.label}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="caption">{option.label}</Typography>
                      {option.ndisNumber && (
                        <Typography variant="caption" color="textSecondary">
                          NDIS: {option.ndisNumber}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Clients List"
                    variant="outlined"
                    fullWidth
                    size="small"
                    error={!selectedClient && !isClientsLoading}
                    helperText={
                      !selectedClient && !isClientsLoading
                        ? 'Please select a client to proceed'
                        : ''
                    }
                  />
                )}
                sx={{
                  '& .MuiAutocomplete-root': {
                    '& .MuiOutlinedInput-root': { borderRadius: '8px' }
                  }
                }}
              />
            )}
          </Box>
        </Grid>
      </Grid>

      {/* User Profile Details */}
      <Grid container spacing={gridSpacing}>
        <Grid item xs={12}>
          <MainCard
            contentSX={{
              p: 1.5,
              paddingBottom: '0px !important',
              [theme.breakpoints.down('lg')]: {
                textAlign: 'center'
              }
            }}
          >
            {isLoading ? (
              <ImagePlaceholder
                sx={{
                  borderRadius: `${borderRadius}px`,
                  overflow: 'hidden',
                  mb: 3,
                  height: { xs: 85, sm: 150, md: 260 }
                }}
              />
            ) : (
              <CardMedia
                component="img"
                image={Cover}
                sx={{
                  borderRadius: `${borderRadius}px`,
                  overflow: 'hidden',
                  mb: 3
                }}
                alt="profile-background"
              />
            )}
            <Grid container spacing={gridSpacing}>
              <Grid item xs={12} md={3}>
                {isLoading ? (
                  <ImagePlaceholder
                    sx={{
                      margin: '-70px 0 0 auto',
                      borderRadius: '16px',
                      [theme.breakpoints.down('lg')]: {
                        margin: '-70px auto 0'
                      },
                      [theme.breakpoints.down('md')]: {
                        margin: '-60px auto 0'
                      },
                      width: { xs: 72, sm: 100, md: 140 },
                      height: { xs: 72, sm: 100, md: 140 }
                    }}
                  />
                ) : (
                  <Avatar
                    alt="User Avatar"
                    src={selectedClient?.avatar || User1}
                    sx={{
                      margin: '-70px 0 0 auto',
                      borderRadius: '16px',
                      [theme.breakpoints.down('lg')]: {
                        margin: '-70px auto 0'
                      },
                      [theme.breakpoints.down('md')]: {
                        margin: '-60px auto 0'
                      },
                      width: { xs: 72, sm: 100, md: 140 },
                      height: { xs: 72, sm: 100, md: 140 }
                    }}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={9}>
                <Grid container spacing={gridSpacing}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="h4">
                      {selectedClient?.label || 'No Client Selected'}
                    </Typography>
                    {selectedClient?.ndisNumber && (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ marginBottom: '8px' }}
                      >
                        NDIS: {selectedClient.ndisNumber}
                      </Typography>
                    )}
                    {userDetails?.role?.map((roleItem: any, index: any) => (
                      <Typography
                        key={roleItem.role_id || index}
                        variant="body2"
                        align="left"
                        color="textSecondary"
                        sx={{ marginBottom: '8px' }}
                      >
                        {roleItem.label}
                      </Typography>
                    ))}
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Grid
                      container
                      spacing={1}
                      sx={{
                        justifyContent: 'flex-end',
                        [theme.breakpoints.down('lg')]: {
                          justifyContent: 'center'
                        }
                      }}
                    >
                      <Grid item>
                        <Button
                          variant="outlined"
                          onClick={handleClickReport}
                          disabled={!selectedClient}
                        >
                          Report All
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button
                          variant="outlined"
                          onClick={handleClickMessages}
                          disabled={!selectedClient}
                        >
                          Message
                        </Button>
                      </Grid>
                      {isAdminLogin && (
                        <Grid item>
                          <Button
                            variant="contained"
                            startIcon={<PersonAddTwoToneIcon />}
                            onClick={handleOpenModal}
                          >
                            Create Staff Members
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container justifyContent="flex-end">
                  <Tabs
                    value={value}
                    variant="scrollable"
                    onChange={handleChange}
                    sx={{
                      marginTop: 2.5,
                      '& .MuiTabs-flexContainer': {
                        border: 'none'
                      },
                      '& a': {
                        minHeight: 'auto',
                        minWidth: 10,
                        py: 1.5,
                        px: 1,
                        mr: 2.25,
                        color:
                          theme.palette.mode === ThemeMode.DARK
                            ? theme.palette.grey[500]
                            : theme.palette.grey[800],
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        transition: 'color 0.3s ease'
                      },
                      '& a.Mui-selected': {
                        color: theme.palette.primary.main,
                        fontWeight: 600
                      },
                      '& a > svg': {
                        marginBottom: '4px !important',
                        mr: 1.25
                      }
                    }}
                  >
                    {tabOptions.map((option, index) => (
                      <Tab
                        key={index}
                        component={Link}
                        to={option?.to}
                        value={option.value}
                        icon={option.icon}
                        label={option.label}
                        disabled={option.isDisable || !selectedClient}
                        {...a11yProps(index)}
                      />
                    ))}
                  </Tabs>
                </Grid>
              </Grid>
            </Grid>
          </MainCard>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ p: 0 }}>
            {selectedClient ? (
              <>
                {value === 'user-profile' && <UserProfile />}
                {value === 'weekly-patients-schedule' && <CalenderEvents />}
                {value === 'managers' && <ListOfManagers />}
                {value === 'workers' && <WorkersList />}
                {value === 'support-workers' && <SupportWorkersList />}
                {value === 'Shift-Notes' && <ShiftNotes />}
                {value === 'office-support' && <OfficeSupport />}
              </>
            ) : (
              <Typography color="error" variant="h6" align="center">
                Please select a client from the dropdown to view content.
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="create-staff-members-modal"
        aria-describedby="modal-to-create-staff-members"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 1, sm: 2 },
          overflowX: 'hidden'
        }}
      >
        <Box
          sx={{
            width: { xs: '95%', sm: '80%', md: 700 },
            maxHeight: { xs: '90dvh', sm: '85dvh', md: '80dvh' },
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxSizing: 'border-box'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: { xs: 1, sm: 1.5 },
              borderBottom: `1px solid ${theme.palette.divider}`
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Add Staff Members
            </Typography>
            <IconButton
              onClick={handleCloseModal}
              size="small"
              sx={{
                '& .MuiSvgIcon-root': {
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Scrollable Content */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              px: { xs: 1, sm: 2 },
              py: 1,
              '&::-webkit-scrollbar': {
                width: '6px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.primary.main,
                borderRadius: '3px'
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: theme.palette.grey[200]
              }
            }}
          >
            <CreateStaffMembers
              onClose={handleCloseModal}
              renderButton={({ handleSubmit, loading, selectedMembersCount }) => (
                <Box
                  sx={{
                    position: 'sticky',
                    bottom: 0,
                    bgcolor: 'background.paper',
                    p: { xs: 1, sm: 2 },
                    borderTop: `1px solid ${theme.palette.divider}`,
                    zIndex: 1
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={loading || selectedMembersCount === 0}
                    sx={{
                      width: '100%',
                      py: { xs: 0.5, sm: 0.75 },
                      fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                      fontWeight: 500,
                      borderRadius: 1,
                      textTransform: 'none',
                      boxShadow: 'none',
                      '&:hover': {
                        boxShadow: 'none'
                      }
                    }}
                  >
                    {loading ? 'Processing...' : `Add Selected (${selectedMembersCount})`}
                  </Button>
                </Box>
              )}
            />
          </Box>
        </Box>
      </Modal>

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarMessage?.includes('no longer available') ? 'warning' : 'error'}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </ClientContext.Provider>
  );
};

export default ProfileMainPage;
